import React, { useMemo } from 'react';
import { Box, ButtonBase, Link, Tooltip, Typography } from '@mui/material';
import { formatHours } from '../monthly/format';
import { formatDateYYYYMMDD, parseLocalDate } from '../../utils/dateHelpers';
import { cardTitleSx, GREEN, GREEN_DARK, INK, LINE, MUTED } from '../home/homeTheme';

// Oltre un mese la barra non sta su una riga: si usa l'intervallo personalizzato
export const MAX_STRIP_DAYS = 31;

/** Numero di giorni tra from e to (YYYY-MM-DD, inclusi). */
export const countDays = (from, to) => Math.round((parseLocalDate(to) - parseLocalDate(from)) / 86400000) + 1;

/** Giorni tra from e to (YYYY-MM-DD, inclusi), al massimo MAX_STRIP_DAYS. */
function daysBetween(from, to) {
  const out = [];
  const end = parseLocalDate(to);
  for (let d = parseLocalDate(from); d <= end && out.length < MAX_STRIP_DAYS; d.setDate(d.getDate() + 1)) {
    out.push({ key: formatDateYYYYMMDD(d), day: d.getDate(), weekday: d.toLocaleDateString('en-GB', { weekday: 'narrow' }), weekend: d.getDay() === 0 || d.getDay() === 6 });
  }
  return out;
}

const longDay = (key) => parseLocalDate(key).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
// Etichetta corta per la cella: decimali solo sotto le 10 ore
const cellHours = (minutes) => (minutes < 600 ? `${formatHours(minutes)}h` : `${Math.round(minutes / 60)}h`);

/**
 * Giorni del periodo con le ore di ciascuno (più ore = verde più intenso).
 * Clic su un giorno = solo quel giorno, Maiusc+clic = intervallo, clic sul giorno scelto = tutti i giorni.
 * value: { from, to, anchor } | null
 */
export default function DayStrip({ from, to, rows, value, onChange }) {
  const days = useMemo(() => daysBetween(from, to), [from, to]);
  const byDay = useMemo(() => {
    const map = {};
    rows.forEach((w) => {
      const t = map[w.dateKey] || { minutes: 0, count: 0 };
      t.minutes += Number(w.effort) || 0;
      t.count += 1;
      map[w.dateKey] = t;
    });
    return map;
  }, [rows]);
  const max = Math.max(1, ...Object.values(byDay).map((t) => t.minutes));
  const todayKey = formatDateYYYYMMDD(new Date());

  const pick = (key, shift) => {
    if (shift && value) {
      const [a, b] = [value.anchor, key].sort();
      onChange({ from: a, to: b, anchor: value.anchor });
    } else if (value && value.from === key && value.to === key) onChange(null);
    else onChange({ from: key, to: key, anchor: key });
  };

  return (
    <Box sx={{ bgcolor: 'var(--rt-paper)', border: `1px solid ${LINE}`, borderRadius: 3, px: 1.5, pt: 1, pb: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.75 }}>
        <Typography sx={{ ...cardTitleSx, fontSize: 13.5 }}>Days</Typography>
        <Typography sx={{ fontSize: 12, color: MUTED, flex: 1 }}>
          {value ? (value.from === value.to ? `Showing ${longDay(value.from)}` : `Showing ${longDay(value.from)} – ${longDay(value.to)}`) : 'Click a day to show only that day · Shift+click to select a range'}
        </Typography>
        {value && (
          <Link component="button" type="button" underline="hover" onClick={() => onChange(null)} sx={{ fontSize: 12.5, color: GREEN_DARK, fontWeight: 600 }}>
            Show all days
          </Link>
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`, gap: 0.5 }}>
        {days.map((d) => {
          const t = byDay[d.key];
          const selected = value && d.key >= value.from && d.key <= value.to;
          const alpha = t ? 0.12 + 0.5 * (t.minutes / max) : 0;
          return (
            <Tooltip key={d.key} title={`${longDay(d.key)} · ${t ? `${formatHours(t.minutes)}h in ${t.count} worklog${t.count === 1 ? '' : 's'}` : 'no worklogs'}`} arrow>
              <ButtonBase
                onClick={(e) => pick(d.key, e.shiftKey)}
                sx={{
                  flexDirection: 'column',
                  height: 46,
                  borderRadius: 1.5,
                  minWidth: 0,
                  bgcolor: selected ? GREEN : t ? `rgba(90,176,49,${alpha.toFixed(2)})` : d.weekend ? 'var(--rt-cal-weekend)' : 'transparent',
                  border: d.key === todayKey ? `2px solid ${INK}` : '1px solid transparent',
                  opacity: value && !selected ? 0.55 : 1,
                  '&:hover': { outline: `1px solid ${GREEN}` },
                }}
              >
                <Typography sx={{ fontSize: 10, lineHeight: 1.1, color: selected ? '#FFFFFF' : MUTED }}>{d.weekday}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2, color: selected ? '#FFFFFF' : d.weekend && !t ? 'var(--rt-faint)' : INK }}>{d.day}</Typography>
                <Typography noWrap sx={{ fontSize: 10.5, lineHeight: 1.1, color: selected ? '#FFFFFF' : INK, fontVariantNumeric: 'tabular-nums', maxWidth: '100%' }}>
                  {t ? cellHours(t.minutes) : ''}
                </Typography>
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
